import { ActionContext, MutationTree, ActionTree, GetterTree } from 'vuex'
import { filter, each, extend, find, cloneDeep, intersection, orderBy } from 'lodash'
import webApi from '../webApi'
import { Report, ReportColumn, ReportListItem, Customer, Country } from '../webApi/DataModels'
import constants from '../constants'

interface IReportListFilter {
  term: string;
  customers: number[];
  countries: number[];
}

class ReportState {
  public reportColumns:ReportColumn[] = []
  public reportList: ReportListItem[] = []
  public filteredReportList: ReportListItem[] = []
  public reportListFilter: IReportListFilter = { term: '', customers: [], countries: [] }
  public loadedReport: Report = null
  public currentEditReport: Report = null
  public reportResult: any = null
}

function resolveValues (ids: number[], values: any[], idFieldName: string, valueFieldName: string, resolvedLabel: string): String {
  var resolvedValues = []
  each(ids, (id) => {
    var filterObject = {}
    filterObject[idFieldName] = id
    var value = find(values, filterObject)
    if (value && value[valueFieldName]) {
      resolvedValues.push(value[valueFieldName])
    }
  })
  if (resolvedValues.length > 3) {
    return resolvedValues.length + ' ' + resolvedLabel
  } else if (resolvedValues.length === 0) {
    return ''
  } else {
    return resolvedValues.join(', ')
  }
}

const mutations = <MutationTree<ReportState>> {
  // sets the report list and resolves the country and customer names
  setReportList (state: ReportState, { list, customers, countries }: { list: ReportListItem[], customers: Customer[], countries: Country[] }) {
    each(list, (reportItem: ReportListItem, index: number) => {
      extend(reportItem, {
        Country_Name: resolveValues(reportItem.Report_Filter.Countries, countries, 'Country_ID', 'Country_Name', 'Countries')
      })

      extend(reportItem, {
        Customer_Name: resolveValues(reportItem.Report_Filter.Customers, customers, 'Customer_ID', 'Customer_Name', 'Customers')
      })
    })

    state.reportList = list
    state.filteredReportList = list
  },
  setLoadedReport (state: ReportState, report: Report): void {
    state.loadedReport = report
  },
  setReportResult (state: ReportState, result: any[]): void {
    state.reportResult = result
  },
  resetReport (state: ReportState): void {
    state.loadedReport = null
  },
  resetReportResult (state: ReportState): void {
    state.reportResult = null
  },
  setReportColumns (state: ReportState, columns: ReportColumn[]): void {
    state.reportColumns = columns
  },
  deletedReport (state: ReportState, report_Id: number) {
    state.reportList = state.reportList.filter((item: ReportListItem) => {
      return item.Report_ID !== report_Id
    })
  },
  // Sets the filter for the report list
  setFilter (state: ReportState, reportListFilter: IReportListFilter): void {
    state.reportListFilter = reportListFilter
    state.filteredReportList = filter(state.reportList, (report) => {
      if (reportListFilter.term.length === 0 && reportListFilter.customers.length === 0 && reportListFilter.countries.length === 0) {
        return true
      }

      var addReport = false
      if (reportListFilter.term.length > 0 && (report.User_Name.toLowerCase().indexOf(reportListFilter.term.toLowerCase()) !== -1 ||
        report.Report_Name.toLowerCase().indexOf(reportListFilter.term.toLowerCase()) !== -1 ||
        (report.Report_Description !== null && report.Report_Description.toLowerCase().indexOf(reportListFilter.term.toLowerCase()) !== -1))) {
        addReport = true
      } else if (intersection(report.Report_Filter.Customers, reportListFilter.customers).length > 0) {
        addReport = true
      } else if (intersection(report.Report_Filter.Countries, reportListFilter.countries).length > 0) {
        addReport = true
      }
      return addReport
    })
  },
  filteredReportList (state:ReportState, { sortFields, sortDirections }) {
    state.filteredReportList = orderBy(state.filteredReportList, sortFields, sortDirections)
    state.reportList = orderBy(state.reportList, sortFields, sortDirections)
  },
  setCurrentEditReport (state: ReportState, report: Report): void {
    state.currentEditReport = report
  },
  reset (state:ReportState) {
    state.reportColumns = []
    state.reportList = []
    state.filteredReportList = []
    state.reportListFilter = { term: '', customers: [], countries: [] }
    state.loadedReport = null
    state.currentEditReport = null
    state.reportResult = null
  }
}

const actions = <ActionTree<ReportState, any>>{
  reset (store: ActionContext<ReportState, any>):void {
    store.commit('reset')
  },
  loadReportColumnSetup (store: ActionContext<ReportState, any>): Promise<any> {
    return store.dispatch('user/hasPermission', { permissionType: constants.PERMISSION_TYPES.REPORT, permissionValue: constants.PERMISSION_VALUES.LIST }, { root: true })
      .then((hasPermission):any => {
        if (!hasPermission) {
          return Promise.resolve('not needed')
        } else {
          return webApi.report.get_Report_Column_Setup().then(
            (response) => {
              store.commit('setReportColumns', response.data)
              return Promise.resolve('success')
            }).catch((response) => {
            window.bus.$emit('openErrorModal', response.data.ExceptionMessage)
            return Promise.reject(response)
          })
        }
      })
  },
  // loads the report list
  loadReportList (store: ActionContext<ReportState, any>): Promise<any> {
    return webApi.report.get_Report_Template_List().then(
      (response) => {
        store.commit('setReportList', {
          list: response.data,
          customers: store.rootState.customer.customers,
          countries: store.rootState.country.countries
        })
        return Promise.resolve('success')
      }).catch((response) => {
      window.bus.$emit('openErrorModal', response.data.ExceptionMessage)
      return Promise.reject(response)
    })
  },
  // loads a report
  loadReport (store: ActionContext<ReportState, any>, reportID: number): Promise<any> {
    store.commit('resetReport')
    return webApi.report.get_Report_Template(reportID).then(
      (response: any) => {
        var result: Report = response.data as Report
        store.commit('setLoadedReport', result)
        return Promise.resolve('success')
      }).catch((response) => {
      window.bus.$emit('openErrorModal', response.data.ExceptionMessage)
      return Promise.reject(response)
    })
  },
  // saves the report to the database
  saveReport (store: ActionContext<ReportState, any>, report: Report): Promise<any> {
    var reportTransport = cloneDeep(report)
    delete reportTransport.Report_Filter.From_Date
    delete reportTransport.Report_Filter.To_Date
    return webApi.report.save_Report_Template(reportTransport).then(
      (response) => {
        return Promise.resolve(response)
      }).catch((response) => {
      window.bus.$emit('openErrorModal', response.data.ExceptionMessage)
      return Promise.reject(response)
    })
  },
  // deletes the report to the database
  deleteReport (store: ActionContext<ReportState, any>, report_Id: number): Promise<any> {
    return webApi.report.delete_Report(report_Id).then(
      (response) => {
        store.commit('deletedReport', report_Id)
        store.commit('setReportList', {
          list: store.state.reportList,
          customers: store.rootState.customer.customers,
          countries: store.rootState.country.countries
        })
        return Promise.resolve(response)
      }).catch((response) => {
      window.bus.$emit('openErrorModal', response.data.ExceptionMessage)
      return Promise.reject(response)
    })
  },
  // executes the report (layout transformation is done when loading the report)
  executeReport (store: ActionContext<ReportState, any>, report: Report): Promise<any> {
    store.commit('resetReportResult')
    var reportLayout = {
      Filter: report.Report_Filter,
      Columns: report.Report_Layout,
      Base_On_Problem_Report: report.Base_On_Problem_Report
    }
    return webApi.report.execute_Report(reportLayout).then(
      (response) => {
        store.commit('setReportResult', response.data)
        return Promise.resolve('success')
      }).catch((error) => {
      window.bus.$emit('openErrorModal', error.response.data)
      return Promise.reject(error)
    })
  },
  setFilter ({ commit }, filter: IReportListFilter) {
    commit('setFilter', filter)
  },
  sortList ({ commit }, { sortFields, sortDirections }): void {
    commit('filteredReportList', { sortFields, sortDirections })
  }
}

const getters = <GetterTree<ReportState, any>>{
  reportList: (state): ReportListItem[] => {
    return state.filteredReportList
  },
  loadedReport: (state): Report => {
    return state.loadedReport
  },
  reportResult: (state): any => {
    return state.reportResult
  }
}

export default ({
  namespaced: true,
  state: new ReportState(),
  mutations,
  actions,
  getters
})
